import type { GeoPoint } from '../dto/geo.dto.js'
import { isPointInRing } from '../helpers/geo.js'

/**
 * Ring validity checks shared by the boundary editor and the API.
 *
 * Coordinates are treated as plane coordinates here, which is legitimate
 * because self-intersection is a TOPOLOGICAL property. Over a few hundred
 * meters the longitude-to-meters factor is effectively constant, so the mapping
 * from lat/lng to the plane is an affine scaling — and affine transforms
 * preserve whether two segments cross. (Area is a different matter entirely,
 * which is why `polygonAreaSquareMeters` does the spherical work.)
 */

/**
 * Sign of the cross product of (b-a) x (c-a): which way you turn going a→b→c.
 * Positive is counterclockwise, negative clockwise, zero collinear.
 */
function orientation(a: GeoPoint, b: GeoPoint, c: GeoPoint): number {
  const value =
    (b.longitude - a.longitude) * (c.latitude - a.latitude) -
    (b.latitude - a.latitude) * (c.longitude - a.longitude)
  if (value > 0) return 1
  if (value < 0) return -1
  return 0
}

/** Whether collinear point `p` lies within segment `a`-`b`'s bounding box. */
function isOnSegment(a: GeoPoint, b: GeoPoint, p: GeoPoint): boolean {
  return (
    Math.min(a.latitude, b.latitude) <= p.latitude &&
    p.latitude <= Math.max(a.latitude, b.latitude) &&
    Math.min(a.longitude, b.longitude) <= p.longitude &&
    p.longitude <= Math.max(a.longitude, b.longitude)
  )
}

/** Whether segments `a`-`b` and `c`-`d` cross or overlap. */
function segmentsIntersect(
  a: GeoPoint,
  b: GeoPoint,
  c: GeoPoint,
  d: GeoPoint,
): boolean {
  const o1 = orientation(a, b, c)
  const o2 = orientation(a, b, d)
  const o3 = orientation(c, d, a)
  const o4 = orientation(c, d, b)

  if (o1 !== o2 && o3 !== o4) return true

  // Collinear overlap: a shared stretch rather than a clean crossing.
  if (o1 === 0 && isOnSegment(a, b, c)) return true
  if (o2 === 0 && isOnSegment(a, b, d)) return true
  if (o3 === 0 && isOnSegment(c, d, a)) return true
  if (o4 === 0 && isOnSegment(c, d, b)) return true

  return false
}

/**
 * Whether a closed ring crosses itself.
 *
 * Worth rejecting rather than tolerating: a figure-of-eight outline has no
 * well-defined interior, so the acreage computed from it is meaningless — and
 * acreage is a number work gets quoted against. Better a boundary that cannot
 * be saved than a site that confidently reports the wrong size.
 *
 * O(n²) over the edges, which is the right complexity for a shape whose vertex
 * count is capped in the tens-to-hundreds and which is validated once per save.
 * Adjacent edges legitimately share an endpoint and are skipped.
 */
export function hasSelfIntersection(vertices: GeoPoint[]): boolean {
  const count = vertices.length
  if (count < 4) return false

  for (let i = 0; i < count; i += 1) {
    const a = vertices[i]
    const b = vertices[(i + 1) % count]
    if (a === undefined || b === undefined) continue

    for (let j = i + 1; j < count; j += 1) {
      // Skip edges sharing an endpoint: consecutive edges, and the wrap-around
      // pair where the last edge meets the first.
      const adjacent = j === i + 1 || (i === 0 && j === count - 1)
      if (adjacent) continue

      const c = vertices[j]
      const d = vertices[(j + 1) % count]
      if (c === undefined || d === undefined) continue

      if (segmentsIntersect(a, b, c, d)) return true
    }
  }

  return false
}

/**
 * Whether any two consecutive vertices are the same point.
 *
 * A zero-length edge is invisible on a map, contributes nothing to the shape,
 * and makes the intersection tests degenerate — it is almost always a
 * double-tap while drawing rather than anything intended.
 */
export function hasRepeatedVertices(vertices: GeoPoint[]): boolean {
  return vertices.some((vertex, index) => {
    const next = vertices[(index + 1) % vertices.length]
    if (next === undefined) return false
    return vertex.latitude === next.latitude && vertex.longitude === next.longitude
  })
}

export type BoundaryProblem = 'SELF_INTERSECTING' | 'REPEATED_VERTEX'

/**
 * The first structural problem with a ring, or null if it is a sound outline.
 *
 * Returns the problem rather than a boolean so the editor can say what is wrong
 * while someone is still dragging, instead of only refusing the save.
 */
export function findBoundaryProblem(
  vertices: GeoPoint[],
): BoundaryProblem | null {
  if (hasRepeatedVertices(vertices)) return 'REPEATED_VERTEX'
  if (hasSelfIntersection(vertices)) return 'SELF_INTERSECTING'
  return null
}

export const BOUNDARY_PROBLEM_MESSAGES: Record<BoundaryProblem, string> = {
  SELF_INTERSECTING:
    'The outline crosses itself. Untangle it before saving — a crossed outline has no area to measure.',
  REPEATED_VERTEX:
    'Two points sit on top of each other. Move or remove one of them.',
}

/**
 * Whether `point` lies on an edge of the ring, including a vertex.
 *
 * Exact, in the plane. A tree a finger placed will not land here; a tree that
 * sits on a fence two areas share will, and that is the case this exists for.
 */
export function isPointOnRingEdge(point: GeoPoint, vertices: GeoPoint[]): boolean {
  const count = vertices.length
  for (let index = 0; index < count; index += 1) {
    const start = vertices[index]
    const end = vertices[(index + 1) % count]
    if (start === undefined || end === undefined) continue
    if (
      orientation(start, end, point) === 0 &&
      isOnSegment(start, end, point)
    ) {
      return true
    }
  }
  return false
}

/**
 * Whether `point` is in the ring's interior.
 *
 * `isPointInRing` will not say which way a point on an edge goes. Callers that
 * are assigning a tree to one area, or deciding that two areas share area,
 * need the edge kept out: a shared fence is not an overlap, and a tree on it
 * is a tie to break rather than a point clearly inside.
 */
export function isPointStrictlyInRing(
  point: GeoPoint,
  vertices: GeoPoint[],
): boolean {
  if (vertices.length < 3) return false
  if (isPointOnRingEdge(point, vertices)) return false
  return isPointInRing(point, vertices)
}

/**
 * Whether segments `a`-`b` and `c`-`d` cross properly.
 *
 * A proper crossing has the two segments pass through each other, so all four
 * orientations are non-zero and the pairs disagree. A shared endpoint, a
 * T-junction, and a collinear stretch along a fence are not proper crossings:
 * two areas are allowed to meet along an edge.
 */
function segmentsProperlyCross(
  a: GeoPoint,
  b: GeoPoint,
  c: GeoPoint,
  d: GeoPoint,
): boolean {
  const o1 = orientation(a, b, c)
  const o2 = orientation(a, b, d)
  const o3 = orientation(c, d, a)
  const o4 = orientation(c, d, b)
  return o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4
}

function ringsProperlyCross(left: GeoPoint[], right: GeoPoint[]): boolean {
  const leftCount = left.length
  const rightCount = right.length
  for (let i = 0; i < leftCount; i += 1) {
    const a = left[i]
    const b = left[(i + 1) % leftCount]
    if (a === undefined || b === undefined) continue
    for (let j = 0; j < rightCount; j += 1) {
      const c = right[j]
      const d = right[(j + 1) % rightCount]
      if (c === undefined || d === undefined) continue
      if (segmentsProperlyCross(a, b, c, d)) return true
    }
  }
  return false
}

function hasStrictlyInteriorVertex(
  probe: GeoPoint[],
  ring: GeoPoint[],
): boolean {
  return probe.some((vertex) => isPointStrictlyInRing(vertex, ring))
}

/**
 * A point just inside the ring, or null when the ring has no area.
 *
 * Used for the one overlap a vertex test misses: two rings drawn on top of
 * each other. Every vertex of one then lies on the other's edge, so none is
 * strictly inside, and the edges never properly cross. Stepping a hair inward
 * from an edge lands in the area both rings claim.
 */
function interiorProbe(vertices: GeoPoint[]): GeoPoint | null {
  let signed = 0
  const count = vertices.length
  for (let index = 0; index < count; index += 1) {
    const start = vertices[index]
    const end = vertices[(index + 1) % count]
    if (start === undefined || end === undefined) continue
    signed +=
      start.longitude * end.latitude - end.longitude * start.latitude
  }
  const counterclockwise = signed >= 0

  for (let index = 0; index < count; index += 1) {
    const start = vertices[index]
    const end = vertices[(index + 1) % count]
    if (start === undefined || end === undefined) continue
    const dLongitude = end.longitude - start.longitude
    const dLatitude = end.latitude - start.latitude
    const length = Math.hypot(dLongitude, dLatitude)
    if (length < 1e-12) continue

    // A step small beside the edge, and never more than about a millimeter
    // of latitude, so a shared fence is not reported as shared area.
    const step = Math.min(length * 1e-4, 1e-8)
    const inwardLongitude = counterclockwise
      ? -dLatitude / length
      : dLatitude / length
    const inwardLatitude = counterclockwise
      ? dLongitude / length
      : -dLongitude / length
    const candidate: GeoPoint = {
      latitude: (start.latitude + end.latitude) / 2 + inwardLatitude * step,
      longitude:
        (start.longitude + end.longitude) / 2 + inwardLongitude * step,
    }
    if (isPointStrictlyInRing(candidate, vertices)) return candidate
  }
  return null
}

/**
 * Whether two rings occupy the same area.
 *
 * Adjacent rings that share an edge, a corner, or a collinear stretch of
 * fence are not an overlap: a site split between two tenants meets along the
 * line between them. A proper crossing is an overlap, as is one ring sitting
 * inside the other, as are two rings drawn on the same ground.
 *
 * O(n²) over the edges. An area is capped at the same few hundred vertices as
 * a site outline, and this runs once per save plus while a corner is dragged.
 */
export function ringsOverlap(left: GeoPoint[], right: GeoPoint[]): boolean {
  if (left.length < 3 || right.length < 3) return false
  if (ringsProperlyCross(left, right)) return true
  if (hasStrictlyInteriorVertex(left, right)) return true
  if (hasStrictlyInteriorVertex(right, left)) return true

  const probe = interiorProbe(left)
  if (probe !== null && isPointStrictlyInRing(probe, right)) return true
  return false
}
